<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Tabs Component
      p.text--secondary A flexible tabs component with line and button styling variants, supporting icons and badges.

    // Tab Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in tabVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
            .mb-3(v-if="variation.id === 'line-basic'")
              strong Selected: {{ lineBasicActive }}
            yz-tabs(v-if="variation.id === 'line-basic'" v-model="lineBasicActive" type="line")
              yz-tab(label="Overview")
              yz-tab(label="Analytics")
              yz-tab(label="Settings")
              yz-tab(label="Support")
            
            .mb-3(v-if="variation.id === 'button-basic'")
              strong Selected: {{ buttonBasicActive }}
            yz-tabs(v-if="variation.id === 'button-basic'" v-model="buttonBasicActive" type="button")
              yz-tab(label="Overview")
              yz-tab(label="Analytics")
              yz-tab(label="Settings")
              yz-tab(label="Support")
            
            .mb-3(v-if="variation.id === 'line-icons'")
              strong Selected: {{ lineIconsActive }}
            yz-tabs(v-if="variation.id === 'line-icons'" v-model="lineIconsActive" type="line")
              yz-tab(label="Dashboard" icon="$yzi-home-03")
              yz-tab(label="Analytics" icon="$yzi-bar-chart-10")
              yz-tab(label="Settings" icon="$yzi-settings-02")
              yz-tab(label="Users" icon="$yzi-users-02")
            
            .mb-3(v-if="variation.id === 'button-icons'")
              strong Selected: {{ buttonIconsActive }}
            yz-tabs(v-if="variation.id === 'button-icons'" v-model="buttonIconsActive" type="button")
              yz-tab(label="Dashboard" icon="$yzi-home-03")
              yz-tab(label="Analytics" icon="$yzi-bar-chart-10")
              yz-tab(label="Settings" icon="$yzi-settings-02")
              yz-tab(label="Users" icon="$yzi-users-02")
            
            .mb-3(v-if="variation.id === 'line-badges'")
              strong Selected: {{ lineBadgesActive }}
            yz-tabs(v-if="variation.id === 'line-badges'" v-model="lineBadgesActive" type="line")
              yz-tab(label="Messages" :badge="12")
              yz-tab(label="Notifications" :badge="3")
              yz-tab(label="Tasks" :badge="24")
              yz-tab(label="Archive")
            
            .mb-3(v-if="variation.id === 'button-badges'")
              strong Selected: {{ buttonBadgesActive }}
            yz-tabs(v-if="variation.id === 'button-badges'" v-model="buttonBadgesActive" type="button")
              yz-tab(label="Messages" :badge="12")
              yz-tab(label="Notifications" :badge="3")
              yz-tab(label="Tasks" :badge="24")
              yz-tab(label="Archive")
            
            div(v-else-if="variation.id === 'line-disabled'")
              .mb-3
                .mb-2
                  strong Selected: {{ lineDisabledActive }}
                yz-tabs(v-model="lineDisabledActive" type="line")
                  yz-tab(label="Dashboard")
                  yz-tab(label="Analytics" disabled)
                  yz-tab(label="Settings")
                  yz-tab(label="Support" disabled)
              .mb-3
                .mb-2
                  strong Selected: {{ buttonDisabledActive }}
                yz-tabs(v-model="buttonDisabledActive" type="button")
                  yz-tab(label="Overview")
                  yz-tab(label="Reports" disabled)
                  yz-tab(label="Users")
                  yz-tab(label="Admin" disabled)
            
            div(v-else-if="variation.id === 'custom-colors'")
              .mb-3
                .mb-2
                  strong Selected: {{ customColorsActive1 }}
                yz-tabs(v-model="customColorsActive1" type="line" color="red-500" badge-color="#bd5b3f" badge-active-color="danger-700")
                  yz-tab(label="Dashboard" icon="$yzi-home-03")
                  yz-tab(label="Messages" icon="$yzi-message-circle-01" :badge="5")
                  yz-tab(label="Analytics" icon="$yzi-bar-chart-10")
              .mb-3
                .mb-2
                  strong Selected: {{ customColorsActive2 }}
                yz-tabs(v-model="customColorsActive2" type="button" color="#ff5722" badge-color="#ff8a65" badge-active-color="#163333" button-hover-color="#f8cdc2")
                  yz-tab(label="Projects" icon="$yzi-folder-01")
                  yz-tab(label="Tasks" icon="$yzi-check-square" :badge="12")
                  yz-tab(label="Settings" icon="$yzi-settings-02")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Complete Example
    .component-section.mb-8#complete-example
      h3.mb-4.cursor-pointer(@click="navigateToSection('complete-example')") Complete Example
      .mb-4
        p A complete implementation showing tabs with content panels:
      
      .variation-item.mb-6
        .variation-preview.mb-4.pa-4.border.rounded
          .mb-3
            strong Selected: {{ completeExampleActive }}
          yz-tabs(v-model="completeExampleActive" type="line")
            yz-tab(label="Dashboard" icon="$yzi-home-03")
            yz-tab(label="Messages" icon="$yzi-message-circle-01" :badge="5")
            yz-tab(label="Analytics" icon="$yzi-bar-chart-10")
            yz-tab(label="Settings" icon="$yzi-settings-02")
          
          .mt-4.pa-3.border.rounded(style="background-color: #fafafa;")
            div(v-if="completeExampleActive === 0") 
              .yz-semibold-body-sm.mb-2 Dashboard Content
              p Welcome to your dashboard. Here you can view an overview of your account and recent activity.
            div(v-if="completeExampleActive === 1")
              .yz-semibold-body-sm.mb-2 Messages Content
              p You have 5 new messages. Check your inbox for the latest updates from your team.
            div(v-if="completeExampleActive === 2")
              .yz-semibold-body-sm.mb-2 Analytics Content  
              p View detailed analytics and reports about your performance and growth metrics.
            div(v-if="completeExampleActive === 3")
              .yz-semibold-body-sm.mb-2 Settings Content
              p Configure your preferences, notifications, and account settings.

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === completeExampleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === completeExampleCode ? 'success' : ''" @click="copyCode(completeExampleCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ completeExampleCode }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      
      .mb-6
        h4.mb-3 YzTabs Props
        .props-table
          v-simple-table
            template(v-slot:default)
              thead
                tr
                  th Prop
                  th Type
                  th Default
                  th Description
              tbody
                tr(v-for="prop in tabsProps" :key="prop.name")
                  td.font-weight-medium.text-no-wrap {{ prop.name }}
                  td.text--secondary.text-no-wrap {{ prop.type }}
                  td.text--secondary.text-no-wrap {{ prop.default }}
                  td.single-line {{ prop.description }}

      .mb-6
        h4.mb-3 YzTab Props
        .props-table
          v-simple-table
            template(v-slot:default)
              thead
                tr
                  th Prop
                  th Type
                  th Default
                  th Description
              tbody
                tr(v-for="prop in tabProps" :key="prop.name")
                  td.font-weight-medium.text-no-wrap {{ prop.name }}
                  td.text--secondary.text-no-wrap {{ prop.type }}
                  td.text--secondary.text-no-wrap {{ prop.default }}
                  td.single-line {{ prop.description }}

    // Events Documentation
    .component-section.mb-8#events
      h3.mb-4.cursor-pointer(@click="navigateToSection('events')") Events
      .events-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Payload
                th Description
            tbody
              tr(v-for="event in tabsEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.payload }}
                td.single-line {{ event.description }}

</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      
      // Active tab states
      lineBasicActive: 0,
      buttonBasicActive: 0,
      lineIconsActive: 0,
      buttonIconsActive: 0,
      lineBadgesActive: 0,
      buttonBadgesActive: 0,
      lineDisabledActive: 0,
      buttonDisabledActive: 0,
      customColorsActive1: 0,
      customColorsActive2: 0,
      completeExampleActive: 0,
      
      tabVariations: [
        {
          name: 'Line Tabs (Default)',
          id: 'line-basic',
          description: 'Traditional tabs with underline indicator',
          code: `yz-tabs(v-model="activeTab" type="line")
  yz-tab(label="Overview")
  yz-tab(label="Analytics")
  yz-tab(label="Settings")
  yz-tab(label="Support")`
        },
        {
          name: 'Button Tabs',
          id: 'button-basic', 
          description: 'Tabs styled as rounded button chips',
          code: `yz-tabs(v-model="activeTab" type="button")
  yz-tab(label="Overview")
  yz-tab(label="Analytics")
  yz-tab(label="Settings")
  yz-tab(label="Support")`
        },
        {
          name: 'Line Tabs with Icons',
          id: 'line-icons',
          description: 'Traditional tabs enhanced with icons',
          code: `yz-tabs(v-model="activeTab" type="line")
  yz-tab(label="Dashboard" icon="$yzi-home-03")
  yz-tab(label="Analytics" icon="$yzi-bar-chart-10")
  yz-tab(label="Settings" icon="$yzi-settings-02")
  yz-tab(label="Users" icon="$yzi-users-02")`
        },
        {
          name: 'Button Tabs with Icons',
          id: 'button-icons',
          description: 'Button style tabs enhanced with icons',
          code: `yz-tabs(v-model="activeTab" type="button")
  yz-tab(label="Dashboard" icon="$yzi-home-03")
  yz-tab(label="Analytics" icon="$yzi-bar-chart-10")
  yz-tab(label="Settings" icon="$yzi-settings-02")
  yz-tab(label="Users" icon="$yzi-users-02")`
        },
        {
          name: 'Line Tabs with Badges',
          id: 'line-badges',
          description: 'Traditional tabs with numerical indicators',
          code: `yz-tabs(v-model="activeTab" type="line")
  yz-tab(label="Messages" :badge="12")
  yz-tab(label="Notifications" :badge="3")
  yz-tab(label="Tasks" :badge="24")
  yz-tab(label="Archive")`
        },
        {
          name: 'Button Tabs with Badges',
          id: 'button-badges',
          description: 'Button style tabs with badge indicators',
          code: `yz-tabs(v-model="activeTab" type="button")
  yz-tab(label="Messages" :badge="12")
  yz-tab(label="Notifications" :badge="3")
  yz-tab(label="Tasks" :badge="24")
  yz-tab(label="Archive")`
        },
        {
          name: 'Disabled Tabs',
          id: 'line-disabled',
          description: 'Tabs with disabled state for both line and button types',
          code: `yz-tabs(v-model="activeTab" type="line")
  yz-tab(label="Dashboard")
  yz-tab(label="Analytics" disabled)
  yz-tab(label="Settings")
  yz-tab(label="Support" disabled)

yz-tabs(v-model="activeTab" type="button")
  yz-tab(label="Overview")
  yz-tab(label="Reports" disabled)
  yz-tab(label="Users")
  yz-tab(label="Admin" disabled)`
        },
        {
          name: 'Custom Colors',
          id: 'custom-colors',
          description: 'Tabs with custom colors, icons, and badge colors using design system tokens',
          code: `yz-tabs(v-model="activeTab" type="line" color="red-500" badge-color="#bd5b3f" badge-active-color="danger-700")
  yz-tab(label="Dashboard" icon="$yzi-home-03")
  yz-tab(label="Messages" icon="$yzi-message-circle-01" :badge="5")
  yz-tab(label="Analytics" icon="$yzi-bar-chart-10")

yz-tabs(v-model="activeTab" type="button" color="#ff5722" badge-color="#ff8a65" badge-active-color="#163333" button-hover-color="#f8cdc2")
  yz-tab(label="Projects" icon="$yzi-folder-01")
  yz-tab(label="Tasks" icon="$yzi-check-square" :badge="12")
  yz-tab(label="Settings" icon="$yzi-settings-02")`
        }
      ],
      
      tabsProps: [
        {
          name: 'type',
          type: 'String',
          default: 'line',
          description: 'Tab styling type. Options: "line" or "button"'
        },
        {
          name: 'color',
          type: 'String', 
          default: 'primary',
          description: 'Color theme for the tabs. Supports hex (#fff), CSS variables (var(--color)), or design tokens (primary-500)'
        },
        {
          name: 'badge-color',
          type: 'String',
          default: 'null',
          description: 'Custom color for tab badges. Supports same formats as color prop'
        },
        {
          name: 'badge-active-color',
          type: 'String',
          default: 'null',
          description: 'Custom color for badges on active tabs. Supports same formats as color prop'
        },
        {
          name: 'button-hover-color',
          type: 'String',
          default: 'neutral-200',
          description: 'Hover color for button-type tabs. Supports same formats as color prop'
        },
        {
          name: 'value',
          type: 'Number',
          default: '0',
          description: 'Currently active tab index (v-model)'
        }
      ],
      
      tabProps: [
        {
          name: 'label',
          type: 'String',
          default: '-',
          description: 'Tab label text'
        },
        {
          name: 'icon',
          type: 'String',
          default: '-',
          description: 'Icon to display in the tab'
        },
        {
          name: 'badge',
          type: 'String|Number',
          default: '-',
          description: 'Badge number to display in the tab'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the tab is disabled'
        }
      ],
      
      tabsEvents: [
        {
          name: 'input',
          payload: 'Number',
          description: 'Emitted when active tab changes (v-model support)'
        },
        {
          name: 'change',
          payload: 'Number',
          description: 'Emitted when tab is changed by user interaction'
        }
      ]
    }
  },
  
  computed: {
    completeExampleCode() {
      return `template(lang="pug")
  div
    yz-tabs(v-model="activeTab" type="line")
      yz-tab(label="Dashboard" icon="$yzi-home-03")
      yz-tab(label="Messages" icon="$yzi-message-circle-01" :badge="5")
      yz-tab(label="Analytics" icon="$yzi-bar-chart-10")
      yz-tab(label="Settings" icon="$yzi-settings-02")
    
    .mt-4.pa-3.border.rounded
      div(v-if="activeTab === 0")
        h4 Dashboard Content
        p Welcome to your dashboard...
      div(v-if="activeTab === 1")
        h4 Messages Content
        p You have 5 new messages...
      // More tab content...

script
  export default {
    data() {
      return {
        activeTab: 0
      }
    }
  }`
    }
  },

  mounted() {
    this.scrollToSection()
  },

  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  
  methods: {
    copyCode(code) {
      navigator.clipboard.writeText(code).then(() => {
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      })
    },
    
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },

    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table, .events-table, .slots-table {
  overflow-x: auto;
}

.props-table .v-data-table,
.events-table .v-data-table,
.slots-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-2 {
  gap: 8px;
}

.gap-2 > * {
  margin: 0;
}
</style>